import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { CheckBox } from '../../../../inputs/CheckBox';
import { Typography } from '../../../../display/Typography';

// Styles
import {
  OptionsControl,
  PresetsRow,
  PresetButton,
  OptionsList,
  OptionRow,
  ChildrenModes,
  ModeRow,
  OptionText,
} from './styles';

// Types + lógica pura
import {
  SurveillanceChildrenMode,
  SurveillanceReportOptions as Options,
  SR_REPORT_PRESET_SUMMARY,
  SR_REPORT_PRESET_FULL,
} from '../../../../../utils/pdf/surveillanceTypes';
import { resolveActivePreset } from '../../../../../utils/pdf/surveillanceReportRows';

// ═══════════════════════════════════════════════════════════════════
// Opciones del informe del Recorregut de Vigilància (2026-07-27).
//
// Presentacional PURO: recibe la selección y emite la siguiente. Ni estado
// propio ni red — así el modal sigue siendo el único dueño de la selección y
// esto se puede montar tal cual desde otro sitio.
//
// Dos presets (Resumen / Completo) que rellenan la lista de casillas, y las
// casillas siempre editables: tocar una deja de coincidir con cualquier
// preset y ninguno queda resaltado.
// ═══════════════════════════════════════════════════════════════════

const CHILDREN_MODES: { mode: Exclude<SurveillanceChildrenMode, 'none'>; key: string }[] = [
  { mode: 'list', key: 'surveillanceReport.childrenList' },
  { mode: 'list-photos', key: 'surveillanceReport.childrenListPhotos' },
  { mode: 'full', key: 'surveillanceReport.childrenFull' },
];

// Cross-link 3 (2026-09-22): mismos tres niveles para los trabajos de las OTs
// vinculadas al recorrido (ficha + primera comunicación + trabajos del equipo).
const LINKED_MODES: { mode: SurveillanceChildrenMode; key: string }[] = [
  { mode: 'list', key: 'surveillanceReport.linkedOrdersList' },
  { mode: 'list-photos', key: 'surveillanceReport.linkedOrdersListPhotos' },
  { mode: 'full', key: 'surveillanceReport.linkedOrdersFull' },
];

interface Props {
  value: Options;
  onChange: (next: Options) => void;
}

export const SurveillanceReportOptions = ({
  value,
  onChange,
}: Props): JSX.Element => {
  const { t } = useTranslation(['modals']);
  const activePreset = resolveActivePreset(value);
  const childrenEnabled = value.children !== 'none';
  const linkedEnabled = value.linkedOrders !== 'none';

  const toggle = (
    key: 'segmentsTable' | 'segmentExtraColumns' | 'causeSummary' | 'segmentPhotos',
  ) =>
    onChange({ ...value, [key]: !value[key] });

  return (
    <OptionsControl role="group" aria-label={t('surveillanceReport.title')}>
      <PresetsRow>
        <PresetButton
          type="button"
          $active={activePreset === 'summary'}
          aria-pressed={activePreset === 'summary'}
          onClick={() => onChange(SR_REPORT_PRESET_SUMMARY)}
        >
          <Typography size="S" variant="semiBold" component="span">
            {t('surveillanceReport.presetSummary')}
          </Typography>
        </PresetButton>
        <PresetButton
          type="button"
          $active={activePreset === 'full'}
          aria-pressed={activePreset === 'full'}
          onClick={() => onChange(SR_REPORT_PRESET_FULL)}
        >
          <Typography size="S" variant="semiBold" component="span">
            {t('surveillanceReport.presetFull')}
          </Typography>
        </PresetButton>
      </PresetsRow>

      <OptionsList>
        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optSegments')}
            checked={value.segmentsTable}
            onChange={() => toggle('segmentsTable')}
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optSegments')}
          </OptionText>
        </OptionRow>

        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optSegmentExtra')}
            checked={value.segmentExtraColumns}
            onChange={() => toggle('segmentExtraColumns')}
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optSegmentExtra')}
          </OptionText>
        </OptionRow>

        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optCauseSummary')}
            checked={value.causeSummary}
            onChange={() => toggle('causeSummary')}
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optCauseSummary')}
          </OptionText>
        </OptionRow>

        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optChildren')}
            checked={childrenEnabled}
            onChange={() =>
              // Marcar arranca en el nivel más barato; desmarcar apaga el bloque.
              onChange({
                ...value,
                children: childrenEnabled ? 'none' : 'list',
              })
            }
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optChildren')}
          </OptionText>
        </OptionRow>

        <ChildrenModes>
          {CHILDREN_MODES.map(({ mode, key }) => (
            <ModeRow key={mode} $disabled={!childrenEnabled}>
              <input
                type="radio"
                name="sr-report-children"
                aria-label={t(key)}
                value={mode}
                disabled={!childrenEnabled}
                checked={value.children === mode}
                onChange={() => onChange({ ...value, children: mode })}
              />
              <OptionText size="S" component="span">
                {t(key)}
              </OptionText>
            </ModeRow>
          ))}
        </ChildrenModes>

        {/* Cross-link 3 (2026-09-22): fotos de los tramos y OTs vinculadas. */}
        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optSegmentPhotos')}
            checked={value.segmentPhotos}
            onChange={() => toggle('segmentPhotos')}
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optSegmentPhotos')}
          </OptionText>
        </OptionRow>

        <OptionRow>
          <CheckBox
            indeterminate={false}
            aria-label={t('surveillanceReport.optLinkedOrders')}
            checked={linkedEnabled}
            onChange={() =>
              onChange({
                ...value,
                linkedOrders: linkedEnabled ? 'none' : 'list',
              })
            }
          />
          <OptionText size="S" component="span">
            {t('surveillanceReport.optLinkedOrders')}
          </OptionText>
        </OptionRow>

        <ChildrenModes>
          {LINKED_MODES.map(({ mode, key }) => (
            <ModeRow key={mode} $disabled={!linkedEnabled}>
              <input
                type="radio"
                name="sr-report-linked"
                aria-label={t(key)}
                value={mode}
                disabled={!linkedEnabled}
                checked={value.linkedOrders === mode}
                onChange={() => onChange({ ...value, linkedOrders: mode })}
              />
              <OptionText size="S" component="span">
                {t(key)}
              </OptionText>
            </ModeRow>
          ))}
        </ChildrenModes>
      </OptionsList>
    </OptionsControl>
  );
};

export default SurveillanceReportOptions;
